import React, { useState } from 'react';
import { Save, X, Building2, Hash, AlertCircle, MapPin, Phone, Mail, Key, Globe, Webhook, Eye, EyeOff, Shield, Zap } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';
import toast from 'react-hot-toast';

const BankForm = ({ onSubmit, onCancel, initialData = null, loading = false }) => {
  const [formData, setFormData] = useState({
    // Dados principais
    name: initialData?.name || '',
    code: initialData?.bank_code || initialData?.code || '',
    
    // Dados bancários
    agency: initialData?.agency || '',
    account: initialData?.account_number || initialData?.account || '',
    digit: initialData?.account_digit || initialData?.digit || '',
    pix_key: initialData?.pix_key || '',
    pix_type: initialData?.pix_type || 'email',
    
    // Dados de contato
    address: initialData?.address || '',
    phone: initialData?.phone || '',
    email: initialData?.email || '',
    website: initialData?.website || '',
    
    // Configurações
    active: initialData?.active !== undefined ? initialData.active : true,
    is_main: initialData?.is_main !== undefined ? initialData.is_main : false,
    
    // Configurações de API
    api_enabled: initialData?.api_config?.enabled || false,
    api_provider: initialData?.api_config?.provider || '',
    api_environment: initialData?.api_config?.environment || 'sandbox',
    api_client_id: initialData?.api_config?.client_id || '',
    api_client_secret: '', // Sempre vazio por segurança
    api_url: initialData?.api_config?.api_url || '',
    api_auth_url: initialData?.api_config?.auth_url || '',
    api_certificate_path: initialData?.api_config?.certificate_path || '',
    api_certificate_password: '', // Sempre vazio por segurança
    api_timeout: initialData?.api_config?.timeout || 30,
    api_retry_attempts: initialData?.api_config?.retry_attempts || 3,
    
    // Configurações de Webhook
    webhook_enabled: initialData?.webhook_config?.enabled || false,
    webhook_url: initialData?.webhook_config?.url || '',
    webhook_secret: '', // Sempre vazio por segurança
    webhook_events: initialData?.webhook_config?.events || ['payment_received', 'transfer_completed'],
    webhook_max_attempts: initialData?.webhook_config?.retry_policy?.max_attempts || 5,
    
    // Observações
    notes: initialData?.notes || ''
  });

  const [errors, setErrors] = useState({});
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showCertPassword, setShowCertPassword] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const pixTypes = [
    { value: 'email', label: 'E-mail' },
    { value: 'phone', label: 'Telefone' },
    { value: 'cpf', label: 'CPF' },
    { value: 'cnpj', label: 'CNPJ' },
    { value: 'random', label: 'Chave Aleatória' }
  ];

  const apiProviders = [];

  const webhookEvents = [
    { value: 'payment_received', label: 'Pagamento Recebido' },
    { value: 'transfer_completed', label: 'Transferência Concluída' },
    { value: 'balance_updated', label: 'Saldo Atualizado' },
    { value: 'account_credited', label: 'Conta Creditada' },
    { value: 'account_debited', label: 'Conta Debitada' },
    { value: 'pix_received', label: 'PIX Recebido' },
    { value: 'pix_sent', label: 'PIX Enviado' },
    { value: 'transaction_failed', label: 'Transação Falhada' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const formatCode = (value) => {
    return value.replace(/\D/g, '').slice(0, 3);
  };

  const formatAgency = (value) => {
    return value.replace(/\D/g, '').slice(0, 6);
  };

  const formatAccount = (value) => {
    return value.replace(/\D/g, '').slice(0, 12);
  };

  const formatDigit = (value) => {
    return value.replace(/[^0-9X]/gi, '').slice(0, 1).toUpperCase();
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value.slice(0, 15);
  };

  const formatPixKey = (value, type) => {
    switch (type) {
      case 'phone':
        return formatPhone(value);
      case 'cpf':
        return value.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4').slice(0, 14);
      case 'cnpj':
        return value.replace(/\D/g, '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5').slice(0, 18);
      case 'email':
        return value.toLowerCase();
      default:
        return value;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome do banco é obrigatório';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'Código do banco é obrigatório';
    } else if (formData.code.length !== 3) {
      newErrors.code = 'Código deve ter 3 dígitos';
    }

    if (formData.agency && formData.agency.length < 4) {
      newErrors.agency = 'Agência deve ter pelo menos 4 dígitos';
    }

    if (formData.account && formData.account.length < 5) {
      newErrors.account = 'Conta deve ter pelo menos 5 dígitos';
    }

    if (formData.pix_key) {
      const { pix_key, pix_type } = formData;
      switch (pix_type) {
        case 'email':
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pix_key)) {
            newErrors.pix_key = 'E-mail inválido';
          }
          break;
        case 'phone':
          if (!/^\(\d{2}\) \d{5}-\d{4}$/.test(pix_key)) {
            newErrors.pix_key = 'Telefone inválido';
          }
          break;
        case 'cpf':
          if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(pix_key)) {
            newErrors.pix_key = 'CPF inválido';
          }
          break;
        case 'cnpj':
          if (!/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(pix_key)) {
            newErrors.pix_key = 'CNPJ inválido';
          }
          break;
      }
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const testApiConnection = async () => {
    if (!formData.api_enabled || !formData.api_url || !formData.api_client_id) {
      toast.error('Preencha as configurações da API antes de testar');
      return;
    }

    setTestingConnection(true);
    try {
      // Simular teste de conexão
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Conexão com a API testada com sucesso!');
    } catch (error) {
      toast.error('Erro ao testar conexão: ' + error.message);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleWebhookEventChange = (eventValue, checked) => {
    setFormData(prev => ({
      ...prev,
      webhook_events: checked 
        ? [...prev.webhook_events, eventValue]
        : prev.webhook_events.filter(e => e !== eventValue)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    // Preparar dados para envio
    const cleanedData = {
      // Dados básicos
      name: formData.name.trim(),
      bank_code: formData.code.replace(/\D/g, ''),
      agency: formData.agency.replace(/\D/g, '') || null,
      account_number: formData.account.replace(/\D/g, '') || null,
      account_digit: formData.digit || null,
      pix_key: formData.pix_key.trim() || null,
      pix_type: formData.pix_key ? formData.pix_type : null,
      
      // Contato
      address: formData.address.trim() || null,
      phone: formData.phone.replace(/\D/g, '') || null,
      email: formData.email.trim() || null,
      website: formData.website.trim() || null,
      
      // Configurações
      active: formData.active,
      is_main: formData.is_main,
      notes: formData.notes.trim() || null,
      
      // Configurações da API (apenas se habilitada)
      api_config: formData.api_enabled ? {
        enabled: true,
        provider: formData.api_provider,
        environment: formData.api_environment,
        client_id: formData.api_client_id,
        client_secret: formData.api_client_secret || undefined, // Não enviar se vazio
        api_url: formData.api_url,
        auth_url: formData.api_auth_url,
        certificate_path: formData.api_certificate_path || null,
        certificate_password: formData.api_certificate_password || undefined,
        timeout: parseInt(formData.api_timeout) || 30,
        retry_attempts: parseInt(formData.api_retry_attempts) || 3,
        scope: ['read', 'write'],
        rate_limit: {
          requests_per_minute: 60,
          requests_per_hour: 1000
        }
      } : null,
      
      // Configurações do Webhook (apenas se habilitado)
      webhook_config: formData.webhook_enabled ? {
        enabled: true,
        url: formData.webhook_url,
        secret: formData.webhook_secret || undefined,
        events: formData.webhook_events,
        retry_policy: {
          max_attempts: parseInt(formData.webhook_max_attempts) || 5,
          backoff_multiplier: 2,
          initial_delay: 1000
        },
        headers: {
          'Content-Type': 'application/json'
        }
      } : null
    };

    onSubmit(cleanedData);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-center justify-between border-b border-gray-200 pb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {initialData ? 'Editar Banco' : 'Cadastrar Banco'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {initialData ? 'Atualize as informações do banco' : 'Preencha os dados do novo banco'}
              </p>
            </div>
          </div>

          {/* Dados Principais */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dados Principais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Nome do Banco"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={errors.name}
                  icon={Building2}
                  placeholder="Ex: Banco do Brasil"
                  required
                />
              </div>
              <div>
                <Input
                  label="Código"
                  name="code"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: formatCode(e.target.value) }))}
                  error={errors.code}
                  icon={Hash}
                  placeholder="001"
                  maxLength={3}
                  required
                />
              </div>
            </div>
          </div>

          {/* Dados Bancários */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dados Bancários</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Input
                  label="Agência"
                  name="agency"
                  value={formData.agency}
                  onChange={(e) => setFormData(prev => ({ ...prev, agency: formatAgency(e.target.value) }))}
                  error={errors.agency}
                  placeholder="1234"
                  maxLength={6}
                />
              </div>
              <div>
                <Input
                  label="Conta"
                  name="account"
                  value={formData.account}
                  onChange={(e) => setFormData(prev => ({ ...prev, account: formatAccount(e.target.value) }))}
                  error={errors.account}
                  placeholder="123456"
                  maxLength={12}
                />
              </div>
              <div>
                <Input
                  label="Dígito"
                  name="digit"
                  value={formData.digit}
                  onChange={(e) => setFormData(prev => ({ ...prev, digit: formatDigit(e.target.value) }))}
                  placeholder="X"
                  maxLength={1}
                />
              </div>
            </div>

            {/* PIX */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3">Chave PIX</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo da Chave
                  </label>
                  <select
                    name="pix_type"
                    value={formData.pix_type}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {pixTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Input
                    label="Chave PIX"
                    name="pix_key"
                    value={formData.pix_key}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      pix_key: formatPixKey(e.target.value, formData.pix_type) 
                    }))}
                    error={errors.pix_key}
                    placeholder={
                      formData.pix_type === 'email' ? 'email@exemplo.com' :
                      formData.pix_type === 'phone' ? '(11) 99999-9999' :
                      formData.pix_type === 'cpf' ? '123.456.789-00' :
                      formData.pix_type === 'cnpj' ? '12.345.678/0001-90' :
                      'Chave aleatória'
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dados de Contato */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dados de Contato</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Endereço"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  icon={MapPin}
                  placeholder="Endereço da agência"
                />
              </div>
              <div>
                <Input
                  label="Telefone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
                  icon={Phone}
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div>
                <Input
                  label="E-mail"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  icon={Mail}
                  placeholder="contato@banco.com.br"
                />
              </div>
              <div>
                <Input
                  label="Website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.banco.com.br"
                />
              </div>
            </div>
          </div>

          {/* Configurações */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Banco ativo
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_main"
                    checked={formData.is_main}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Banco principal
                  </span>
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Observações sobre o banco..."
                />
              </div>
            </div>
          </div>

          {/* Configurações de API */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Key className="w-5 h-5 mr-2" />
                Integração com API Bancária
              </h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="api_enabled"
                  checked={formData.api_enabled}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Habilitar integração
                </span>
              </label>
            </div>

            {formData.api_enabled && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ambiente
                    </label>
                    <select
                      name="api_environment"
                      value={formData.api_environment}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="sandbox">Sandbox (Teste)</option>
                      <option value="production">Produção</option>
                    </select>
                  </div>

                  <div>
                    <Input
                      label="Client ID"
                      name="api_client_id"
                      value={formData.api_client_id}
                      onChange={handleChange}
                      placeholder="seu_client_id_aqui"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Secret
                    </label>
                    <div className="relative">
                      <input
                        type={showApiSecret ? 'text' : 'password'}
                        name="api_client_secret"
                        value={formData.api_client_secret}
                        onChange={handleChange}
                        placeholder={initialData ? 'Deixe vazio para manter o atual' : 'seu_client_secret_aqui'}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiSecret(!showApiSecret)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showApiSecret ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Input
                      label="URL da API"
                      name="api_url"
                      value={formData.api_url}
                      onChange={handleChange}
                      placeholder="https://api.banco.com.br"
                      icon={Globe}
                    />
                  </div>

                  <div>
                    <Input
                      label="URL de Autenticação"
                      name="api_auth_url"
                      value={formData.api_auth_url}
                      onChange={handleChange}
                      placeholder="https://auth.banco.com.br/oauth2/token"
                      icon={Shield}
                    />
                  </div>

                  <div>
                    <Input
                      label="Caminho do Certificado"
                      name="api_certificate_path"
                      value={formData.api_certificate_path}
                      onChange={handleChange}
                      placeholder="/path/to/cert.p12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha do Certificado
                    </label>
                    <div className="relative">
                      <input
                        type={showCertPassword ? 'text' : 'password'}
                        name="api_certificate_password"
                        value={formData.api_certificate_password}
                        onChange={handleChange}
                        placeholder={initialData ? 'Deixe vazio para manter o atual' : 'senha_do_certificado'}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCertPassword(!showCertPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showCertPassword ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Input
                      label="Timeout (segundos)"
                      name="api_timeout"
                      type="number"
                      value={formData.api_timeout}
                      onChange={handleChange}
                      min="1"
                      max="300"
                      placeholder="30"
                    />
                  </div>

                  <div>
                    <Input
                      label="Tentativas de Retry"
                      name="api_retry_attempts"
                      type="number"
                      value={formData.api_retry_attempts}
                      onChange={handleChange}
                      min="1"
                      max="10"
                      placeholder="3"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-blue-200">
                  <div className="text-sm text-blue-700">
                    <Shield className="w-4 h-4 inline mr-1" />
                    As credenciais são criptografadas antes do armazenamento
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={testApiConnection}
                    disabled={testingConnection}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {testingConnection ? 'Testando...' : 'Testar Conexão'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Configurações de Webhook */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Webhook className="w-5 h-5 mr-2" />
                Configurações de Webhook
              </h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="webhook_enabled"
                  checked={formData.webhook_enabled}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Habilitar webhooks
                </span>
              </label>
            </div>

            {formData.webhook_enabled && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="URL do Webhook"
                      name="webhook_url"
                      value={formData.webhook_url}
                      onChange={handleChange}
                      placeholder="https://seu-dominio.com/webhook/banks"
                      icon={Globe}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secret Key
                    </label>
                    <div className="relative">
                      <input
                        type={showWebhookSecret ? 'text' : 'password'}
                        name="webhook_secret"
                        value={formData.webhook_secret}
                        onChange={handleChange}
                        placeholder={initialData ? 'Deixe vazio para manter o atual' : 'webhook_secret_key'}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showWebhookSecret ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Input
                      label="Máximo de Tentativas"
                      name="webhook_max_attempts"
                      type="number"
                      value={formData.webhook_max_attempts}
                      onChange={handleChange}
                      min="1"
                      max="20"
                      placeholder="5"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Eventos para Notificar
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {webhookEvents.map(event => (
                      <label key={event.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.webhook_events.includes(event.value)}
                          onChange={(e) => handleWebhookEventChange(event.value, e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {event.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-purple-200">
                  <div className="text-sm text-purple-700">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Webhooks são úteis para sincronização automática de dados bancários
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : initialData ? 'Atualizar' : 'Cadastrar'}
            </Button>
          </div>

          {/* Alertas */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                <div>
                  <h4 className="font-medium text-red-800">
                    Corrija os seguintes erros:
                  </h4>
                  <ul className="text-sm text-red-700 mt-1">
                    {Object.values(errors).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};

export default BankForm;